// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { sGetRaw, sSetRaw, sRemove } from "../lib/scopedStorage";
import type { UserProfile } from "../types";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import { DEFAULT_PRESETS, buildProjectFromPreset, LS_PRESETS_KEY_EXPORT } from "../projectData";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import OELogo from "./OELogo";
import OEWordmark from "./OEWordmark";
import ShareDialog from "./ShareDialog";
import BudgetView from "./BudgetView";
import BulkImportDialog from "./BulkImportDialog";
import { hasBudgetBaseline, saveBudget, buildSandboxBudget } from "../lib/budget";
import { PrivacyModal } from "./PrivacyModal";
import NotificationBell from "./NotificationBell";
import * as Icon from "./OEIcons";
import { accountMenuEntries } from "../lib/accountMenu";
import { BUDGET_ENABLED } from "../lib/featureFlags";
import { validateCoverFile } from "../lib/coverPhoto";
import CoverPhotoEditor from "./CoverPhotoEditor";
import ProjectAssistant from "./ProjectAssistant";
import OrgDirectory from "./OrgDirectory";
import HelpCenter from "./HelpCenter";
import { useEntitlements } from "../hooks/useEntitlements";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const IMPORT_SKIP_KEY = "oe_import_skipped_v1";



function fmt(n: number) {
  if (!n) return "$0";
  return "$" + Math.round(n).toLocaleString();
}

function relativeDate(iso: string) {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function cardAccent(name: string, isDark: boolean): { bg: string; iconColor: string } {
  const DARK_BG  = ["#0e2238","#0e2a16","#2a2208","#2a0e0e","#0e1632","#1e0e2a"];
  const DARK_IC  = ["#4a8fbf","#4abf6a","#bfb04a","#bf6a4a","#6a7abf","#8a6abf"];
  const LIGHT_BG = ["#deeaf5","#dff0e5","#faf3df","#fdf0ee","#e8eaf8","#f2e8f8"];
  const LIGHT_IC = ["#2878b8","#28984a","#c8940a","#c85a28","#5060a8","#885ac8"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  const idx = Math.abs(h) % 6;
  return isDark ? { bg: DARK_BG[idx], iconColor: DARK_IC[idx] } : { bg: LIGHT_BG[idx], iconColor: LIGHT_IC[idx] };
}

interface ProjectSummary {
  id: string;
  name: string;
  address: string;
  total: number;
  costPerSF: number;
  savedAt: string;
  snapshotCount: number;
  ownerId: string;
  ownerName: string;
  role: string;
  orgId: string | null;
  orgName: string | null;
  sharedVia: "owner" | "org" | "share";
  isActive: boolean;
  /** Path into the project-photos bucket, not the image itself. */
  photoPath: string | null;
}

interface Props {
  storage: SupabaseStorageProvider;
  user: UserProfile;
  userOrgs: Array<{ id: string; name: string }>;
  onOpen: (id: string, name: string, orgId?: string | null) => void;
  onOpenSnapshot: (projectId: string, projectName: string, state: any) => void;
  onOpenSettings: (tab: string) => void;
  onSandbox: () => void;
  onSignOut: () => void;
  onOpenConceptualBudget: (id: string) => void;
  onOpenInsights: () => void;
  onFirstLoad?: () => void;
}

export default function ProjectPicker({ storage, user, userOrgs, onOpen, onOpenSnapshot, onOpenSettings, onSandbox, onSignOut, onOpenConceptualBudget, onOpenInsights, onFirstLoad }: Props) {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;
  const entitlements = useEntitlements(user?.id);
  useEffect(() => { document.body.style.background = T.bgApp; }, [isDarkMode]);

  // Responsive breakpoints
  const [winW, setWinW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = winW < 640;
  const isTablet = winW < 1024;
  const hPad = isMobile ? "16px" : isTablet ? "24px" : "32px";

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [budgetView, setBudgetView] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [showNewInput, setShowNewInput] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [importBanner, setImportBanner] = useState(false);
  const [localFiles, setLocalFiles] = useState<Record<string, any>>({});
  const [importing, setImporting] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState("preset_sfr");
  const [presets, setPresets] = useState<any[]>(() => {
    try { const s = sGetRaw(LS_PRESETS_KEY_EXPORT); return s ? JSON.parse(s) : DEFAULT_PRESETS; } catch { return DEFAULT_PRESETS; }
  });
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [projectSnapshots, setProjectSnapshots] = useState<Record<string, any[]>>({});
  const [aiImporting, setAiImporting] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const aiFileRef = useRef<HTMLInputElement>(null);
  const [showAiImportModal, setShowAiImportModal] = useState(false);
  const [aiImportName, setAiImportName] = useState("");
  const [aiImportAddress, setAiImportAddress] = useState("");
  const [aiImportDuration, setAiImportDuration] = useState("12");
  const [aiImportSF, setAiImportSF] = useState("");
  const [aiImportPresetId, setAiImportPresetId] = useState("preset_sfr");
  const [aiImportFile, setAiImportFile] = useState<File | null>(null);
  const [aiImportDragging, setAiImportDragging] = useState(false);
  const aiImportFileRef = useRef<HTMLInputElement>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [sharingProject, setSharingProject] = useState<ProjectSummary | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<"active" | "inactive" | "all">("active");
  const [showAssistant, setShowAssistant] = useState(false);
  const [showOrgDirectory, setShowOrgDirectory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // ── Conceptual Budgets (global, not per-project) ──
  const CB_KEY = "openestimate_conceptual_budgets_v1";
  interface CSiteItem { id: string; name: string; low: number; high: number }
  interface CSubArea { id: string; name: string; sf: number }
  interface CAreaGroup { id: string; name: string; subAreas: CSubArea[]; lowRate: number; highRate: number }
  interface CBudget { id: string; name: string; savedAt: string; buildings?: any[]; rates?: number[]; areaGroups?: CAreaGroup[]; siteItems: CSiteItem[]; notes: string; isActive?: boolean; deletedAt?: string }
  const [cBudgets, setCBudgets] = useState<CBudget[]>(() => {
    try { const s = sGetRaw(CB_KEY); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [cbMenuOpenId, setCbMenuOpenId] = useState<string | null>(null);
  const [renamingBudgetId, setRenamingBudgetId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const [deletedProjects, setDeletedProjects] = useState<Array<{ id: string; name: string; address: string; deletedAt: string }>>([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [permDeleteId, setPermDeleteId] = useState<string | null>(null);
  useEffect(() => {
    try { sSetRaw(CB_KEY, JSON.stringify(cBudgets)); } catch {}
  }, [cBudgets]);
  const createBudget = () => {
    const id = "cb" + Date.now();
    const newBudget: CBudget = {
      id, name: "New Budget", savedAt: new Date().toISOString(),
      areaGroups: [
        { id: id + "_g1", name: "Conditioned Space", subAreas: [{ id: id + "_g1_1", name: "First Floor", sf: 0 }, { id: id + "_g1_2", name: "Second Floor", sf: 0 }], lowRate: 0, highRate: 0 },
      ],
      siteItems: [], notes: "", isActive: true,
    };
    const updated = [...cBudgets, newBudget];
    setCBudgets(updated);
    try { sSetRaw(CB_KEY, JSON.stringify(updated)); } catch {}
    onOpenConceptualBudget(id);
  };

  const initials = (user.displayName || user.email || "?").split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2);

  // Signed URLs for the cover photos, keyed by storage path. Signed in one
  // batch after the list loads rather than one request per card.
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  // Which project a chosen file belongs to. The input is a single hidden
  // element reused by every card.
  const coverForRef = useRef<{ id: string; oldPath: string | null } | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [coverBusy, setCoverBusy] = useState<string | null>(null);
  const [editingCover, setEditingCover] = useState<{ id: string; oldPath: string | null; file: File } | null>(null);

  const pickCoverPhoto = (id: string, oldPath: string | null) => {
    coverForRef.current = { id, oldPath };
    if (coverInputRef.current) {
      coverInputRef.current.value = "";   // re-picking the same file must fire
      coverInputRef.current.click();
    }
  };

  const onCoverChosen = (file: File | undefined) => {
    const target = coverForRef.current;
    coverForRef.current = null;
    if (!file || !target) return;
    const problem = validateCoverFile(file);
    if (problem) { alert(problem); return; }
    // The editor decides the crop; nothing is uploaded until it is applied.
    setEditingCover({ ...target, file });
  };

  const uploadCover = async (blob: Blob) => {
    const target = editingCover;
    setEditingCover(null);
    if (!target) return;
    setCoverBusy(target.id);
    try {
      const path = await storage.uploadProjectPhoto(target.id, blob);
      await storage.setProjectPhoto(target.id, path);
      if (target.oldPath) await storage.deleteProjectPhoto(target.oldPath);
      await load();
    } catch (e: any) {
      console.error("[cover photo]", e);
      alert(e?.message || "Could not set that cover photo.");
    } finally {
      setCoverBusy(null);
    }
  };

  const removeCoverPhoto = async (id: string, path: string) => {
    setCoverBusy(id);
    try {
      await storage.setProjectPhoto(id, null);
      await storage.deleteProjectPhoto(path);
      await load();
    } catch (e: any) {
      alert(e?.message || "Could not remove that cover photo.");
    } finally {
      setCoverBusy(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const list = await storage.listProjectsAsync();
    const paths = list.map((p: any) => p.photoPath).filter(Boolean) as string[];
    storage.signProjectPhotos(paths).then(setPhotoUrls).catch(() => setPhotoUrls({}));
    setProjects(list);
    setLoading(false);
    onFirstLoad?.();

    // Check for local data to import
    const skipped = sGetRaw(IMPORT_SKIP_KEY);
    if (!skipped) {
      try {
        const raw = sGetRaw("openestimate_files_v2");
        const files = raw ? JSON.parse(raw) : {};
        const keys = Object.keys(files);
        if (keys.length > 0 && list.length === 0) {
          setLocalFiles(files);
          setImportBanner(true);
        }
      } catch {}
    }
  }, [storage]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (projectId: string) => {
    if (expandedProjectId === projectId) { setExpandedProjectId(null); return; }
    setExpandedProjectId(projectId);
    if (!projectSnapshots[projectId]) {
      const snaps = await storage.listSnapshots(projectId);
      setProjectSnapshots(prev => ({ ...prev, [projectId]: snaps }));
    }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    const preset = presets.find((p: any) => p.id === selectedPresetId) || presets[0] || DEFAULT_PRESETS[0];
    const state = buildProjectFromPreset(name, preset);
    if (newAddress.trim()) (state as any).address = newAddress.trim();
    const id = await storage.createProject(state, selectedOrgId || undefined);
    setCreating(false);
    if (id) { setShowNewInput(false); setNewName(""); setNewAddress(""); setSelectedOrgId(""); onOpen(id, name, selectedOrgId || null); } else { alert("Failed to create project"); }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const result = await storage.deleteProjectById(id);
    setDeletingId(null);
    if (result.error) {
      alert(`Delete failed: ${result.error}\n\nCheck the browser console for details.`);
      return;
    }
    setConfirmDeleteId(null);
    load();
  };

  const handleImport = async () => {
    setImporting(true);
    const entries = Object.entries(localFiles);
    for (const [name, state] of entries) {
      await storage.createProject({ ...state as any, projectName: name });
    }
    // Remove from localStorage
    sRemove("openestimate_files_v2");
    setImportBanner(false);
    setImporting(false);
    load();
  };

  const handleSkipImport = () => {
    sSetRaw(IMPORT_SKIP_KEY, "1");
    setImportBanner(false);
  };

  const runAiImport = async (file: File, meta: { name: string; address: string; duration: number; sf: number; presetId: string }) => {
    if (file.size > 3 * 1024 * 1024) { alert("File too large — 3 MB maximum"); return; }
    setShowAiImportModal(false);
    setAiImporting(true);
    try {
      const { supabase: sb } = await import("../lib/supabase");
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.access_token) { alert("Please sign in to use AI Import"); setAiImporting(false); return; }

      // Same pipeline the history importer uses. It lived here as seventy
      // inline lines, which is how a second caller becomes a second copy.
      const { importEstimateFile } = await import("../lib/estimateImport");
      const { projectState: result, warnings: importWarnings } =
        await importEstimateFile(file, session.access_token);

      if (!result) throw new Error("No result received from AI");
      // Surface server-side sanitization results so the user reviews flagged items
      if (importWarnings && (importWarnings.invalidNumbers || importWarnings.suspectValues || importWarnings.duplicatesRemoved)) {
        const parts: string[] = [];
        if (importWarnings.invalidNumbers) parts.push(`${importWarnings.invalidNumbers} invalid number(s) reset to 0`);
        if (importWarnings.suspectValues) parts.push(`${importWarnings.suspectValues} unusually large value(s)`);
        if (importWarnings.duplicatesRemoved) parts.push(`${importWarnings.duplicatesRemoved} duplicate line(s) removed`);
        alert(`Import completed with warnings:\n• ${parts.join("\n• ")}\n\nAffected items are marked "Needs Review" — please verify them in the Estimate tab.`);
      }
      // Apply user-provided metadata over AI result
      const namePart = meta.name.trim();
      const addrPart = meta.address.trim();
      if (namePart) result.projectName = namePart + (addrPart ? ` — ${addrPart}` : "");
      if (meta.duration > 0) result.duration = meta.duration;
      if (meta.sf > 0) result.buildingArea = meta.sf;
      // Apply preset overhead/profit/contingency if user chose one
      const chosenPreset = presets.find((p: any) => p.id === meta.presetId);
      if (chosenPreset) {
        if (!result.overhead) result.overhead = chosenPreset.overhead || 10;
        if (!result.profit) result.profit = chosenPreset.profit || 8;
        if (!result.contingency) result.contingency = chosenPreset.contingency || 5;
      }
      const newId = await storage.createProject(result);
      if (newId) {
        onOpen(newId, result.projectName || file.name, null);
      } else throw new Error("Failed to save project");
    } catch (err: any) {
      console.error("AI import error:", err);
      alert(err.message || "Import failed — please try again");
    } finally { setAiImporting(false); }
  };

  const handleAiImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    await runAiImport(file, { name: "", address: "", duration: 12, sf: 0, presetId: selectedPresetId });
  };

  const localCount = Object.keys(localFiles).length;

  if (BUDGET_ENABLED && budgetView) {
    return <BudgetView projectId={budgetView.id} projectName={budgetView.name} darkMode={isDarkMode} onClose={() => setBudgetView(null)} />;
  }

  return (
    <div style={{ fontFamily: FONT, background: T.bgApp, minHeight: "100vh", color: T.textSecondary }}
      onClick={() => setShowUserMenu(false)}>
      {/* Header */}
      <div style={{ background: T.bgHeader, borderBottom: `1px solid ${T.borderPrimary}`, height: 56 }}>
      <div style={{ maxWidth: isMobile ? "100%" : 1280, width: "100%", margin: "0 auto", padding: `0 ${hPad}`, height: "100%", display: "flex", alignItems: "center", boxSizing: "border-box" as any }}>
        {/* Left: OE graphic mark */}
        <OELogo
          height={20}
          circleColor="white"
          barColor={T.borderAccent}
        />
        {/* Center: wordmark text only */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <OEWordmark
            size={20}
            showMark={false}
            showSub={false}
            wordColor={T.textInverse}
            accentColor={T.borderAccent}
            onClick={() => window.history.pushState(null, "", "#/")}
            title="Home"
          />
        </div>
        {/* Right cluster */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => setShowAssistant(true)}
            style={{ background: `${T.borderAccent}18`, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent, cursor: "pointer", padding: "7px 16px", borderRadius: 6, fontSize: 12, fontFamily: FONT, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            title="Ask questions about your projects">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M14 2H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3v2l3-2h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zm-5 7H5V8h4v1zm2-2H5V6h6v1z"/></svg>
            Ask AI
          </button>
          {/* Avatar */}
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowUserMenu(v => !v)}
              style={{ width: 32, height: 32, borderRadius: "50%", background: T.bgAccentLight, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent, cursor: "pointer", fontSize: 12, fontFamily: FONT, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}
              title={user.displayName || user.email}>
              {user.avatarUrl ? <img src={user.avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} /> : initials}
            </button>
            {showUserMenu && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, minWidth: 210, maxWidth: `calc(100vw - 32px)`, zIndex: 100, boxShadow: `0 8px 24px ${T.bgModalBackdrop}`, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.borderPrimary}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary }}>{user.displayName}</div>
                  <div style={{ fontSize: 11, color: T.textWeak, marginTop: 2 }}>{user.email}</div>
                </div>
                <div style={{ padding: "4px 0" }}>
                  <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div onClick={() => { const next = !isDarkMode; setIsDarkMode(next); try { sSetRaw("oe_dark_mode", String(next)); } catch {} }}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: !isDarkMode ? T.textSecondary : T.textMuted, transition: "color .2s" }}>LIGHT</span>
                      <div style={{ width: 44, height: 22, borderRadius: 999, background: isDarkMode ? "#3a3d44" : "#c8cad0", position: "relative", transition: "background .25s", flexShrink: 0 }}>
                        <div style={{ width: 18, height: 18, borderRadius: 999, background: "#fff", position: "absolute", top: 2, left: isDarkMode ? 24 : 2, transition: "left .25s", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: isDarkMode ? T.textSecondary : T.textMuted, transition: "color .2s" }}>DARK</span>
                    </div>
                  </div>
                  {accountMenuEntries({ surface: "picker", canUseInsights: entitlements.canUseInsights })
                    .map(({ id, label, icon, settingsTab }) => (
                    <button key={id} onClick={() => {
                      setShowUserMenu(false);
                      if (settingsTab) onOpenSettings(settingsTab);
                      else if (id === "insights") onOpenInsights();
                      else if (id === "directory") setShowOrgDirectory(true);
                      else if (id === "help") setShowHelp(true);
                    }}
                      style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "8px 16px", fontSize: 12, color: T.textTertiary, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 10 }}>
                      {icon} {label}
                    </button>
                  ))}
                </div>
                <div style={{ height: 1, background: T.borderPrimary }} />
                <div style={{ padding: "4px 0" }}>
                  <button onClick={() => { setShowUserMenu(false); setShowPrivacyModal(true); }}
                    style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "8px 16px", fontSize: 12, color: T.textTertiary, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon.Lock size={14}/> Privacy &amp; Data
                  </button>
                  <button onClick={() => { setShowUserMenu(false); window.location.href = "mailto:feedback@open-estimate.com?subject=OpenEstimate%20Feedback"; }}
                    style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "8px 16px", fontSize: 12, color: T.textTertiary, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon.Envelope size={14}/> Send Feedback
                  </button>
                  <button onClick={onSignOut}
                    style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "9px 16px", fontSize: 12, color: T.negative, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon.ArrowLeft size={14}/> Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Full-bleed hero banner — always dark so it "bleeds" in light mode too */}
      <div style={{ background: T.bgHeader, borderBottom: `1px solid rgba(255,255,255,0.07)` }}>
        <div style={{ maxWidth: isMobile ? "100%" : 1280, width: "100%", margin: "0 auto", padding: isMobile ? `20px 16px` : isTablet ? `24px 24px` : `28px 32px`, boxSizing: "border-box" as any }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#4fd1c5", letterSpacing: ".14em", textTransform: "uppercase" as any, marginBottom: 8 }}>Your Projects</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#e2ddd5", letterSpacing: "-.025em", lineHeight: 1, display: "flex", alignItems: "baseline", gap: 10 }}>
                Projects
                <span style={{ fontSize: 24, color: "#8a8680", fontWeight: 400 }}>· {projects.length}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as any }}>
              {/* Search — lives in the banner on the same row as filters */}
              {!isMobile && (
                <div style={{ position: "relative", marginRight: 4 }}>
                  <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.35)", pointerEvents: "none", display: "flex" }}>
                    <Icon.Search size={13}/>
                  </span>
                  <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === "Escape") setSearchQuery(""); }}
                    placeholder="Search projects..."
                    style={{ width: 220, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, color: "#e2ddd5", padding: "7px 12px 7px 32px", fontSize: 12, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any, transition: "border-color .15s" }}
                    onFocus={e => e.currentTarget.style.borderColor = "rgba(79,209,197,0.5)"}
                    onBlur={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)"; setTimeout(() => setSearchQuery(""), 150); }}
                  />
                  {searchQuery && (() => {
                    const q = searchQuery.toLowerCase();
                    const matches = projects.filter(p => p.name.toLowerCase().includes(q) || (p.address || "").toLowerCase().includes(q)).slice(0, 8);
                    return (
                      <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200, background: T.bgPanel, border: `1px solid ${T.borderHover}`, borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
                        {matches.length === 0
                          ? <div style={{ padding: "12px 14px", fontSize: 12, color: T.textWeak, fontStyle: "italic" }}>No projects match "{searchQuery}"</div>
                          : matches.map(p => (
                            <div key={p.id}
                              onMouseDown={() => { onOpen(p.id, p.name, p.orgId || null); setSearchQuery(""); }}
                              style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", borderBottom: `1px solid ${T.borderSecondary}` }}
                              onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                              <div style={{ width: 28, height: 28, borderRadius: 6, background: cardAccent(p.name, isDarkMode).bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: cardAccent(p.name, isDarkMode).iconColor, letterSpacing: ".04em" }}>
                                  {p.name.split(/\s+/).slice(0, 2).map((w: string) => w[0]).join("").toUpperCase()}
                                </span>
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: T.textSubLabel, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                                {p.address && <div style={{ fontSize: 10, color: T.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.address}</div>}
                              </div>
                              <div style={{ marginLeft: "auto", flexShrink: 0 }}>
                                {p.isActive ? <div style={{ fontSize: 10, color: "#4abf6a", fontWeight: 700 }}>● Active</div> : <div style={{ fontSize: 10, color: T.textWeak }}>○ Inactive</div>}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    );
                  })()}
                </div>
              )}
              <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.12)", margin: "0 2px" }}/>
              {(["active", "inactive", "all"] as const).map(f => {
                const on = activeFilter === f;
                const label = f === "active" ? "Active" : f === "inactive" ? "Inactive" : "All";
                return (
                  <button key={f} onClick={() => setActiveFilter(f)}
                    style={{ background: on ? "rgba(79,209,197,0.12)" : "none", border: on ? `1px solid rgba(79,209,197,0.35)` : `1px solid rgba(255,255,255,0.14)`, borderRadius: 3, color: on ? "#4fd1c5" : "rgba(255,255,255,0.45)", cursor: "pointer", padding: "7px 18px", fontSize: 13, fontFamily: FONT, fontWeight: on ? 700 : 400, transition: "all .15s" }}>
                    {label}
                  </button>
                );
              })}
              <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.12)", margin: "0 2px" }}/>
              <button onClick={async () => { setShowTrash(true); setLoadingTrash(true); const del = await storage.listDeletedProjects(); setDeletedProjects(del); setLoadingTrash(false); }}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", cursor: "pointer", fontSize: 12, fontFamily: FONT, padding: "5px 8px", display: "flex", alignItems: "center", gap: 5 }}
                onMouseEnter={e => (e.currentTarget.style.color = "rgba(255,255,255,0.7)")}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}>
                <Icon.Trash size={14}/> Trash
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: isMobile ? "100%" : 1280, width: "100%", margin: "0 auto", padding: isMobile ? "24px 16px" : isTablet ? "32px 24px" : "32px 32px", boxSizing: "border-box" }}>

        {/* Import banner */}
        {importBanner && (
          <div style={{ background: "#1e1a0e", border: "1px solid #4a3a10", borderRadius: 6, padding: "14px 18px", marginBottom: 28, display: "flex", alignItems: isMobile ? "flex-start" : "center", flexWrap: "wrap", justifyContent: "space-between", gap: 16 }}>
            <div>
              <span style={{ color: T.borderAccent, fontSize: 13, fontWeight: 600 }}>Import local estimates</span>
              <span style={{ color: "#a08858", fontSize: 12, marginLeft: 10 }}>
                You have {localCount} estimate{localCount !== 1 ? "s" : ""} saved on this device — import to cloud?
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={handleSkipImport}
                style={{ background: "none", border: "1px solid #3a3020", color: "#7a6848", cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>
                Skip
              </button>
              <button onClick={handleImport} disabled={importing}
                style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: importing ? "wait" : "pointer", padding: "5px 14px", borderRadius: 3, fontSize: 11, fontFamily: FONT, fontWeight: 700, opacity: importing ? 0.6 : 1 }}>
                {importing ? "Importing..." : "Import All"}
              </button>
            </div>
          </div>
        )}

        {/* Portfolio Insights lived in the avatar menu -- a dropdown built for
            account settings, which is not where anyone looks for analysis of
            their own work. It reads every project you can see, so it belongs
            beside the projects it reads. */}
        {entitlements.canUseInsights && !loading && projects.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
            <button onClick={() => onOpenInsights()}
              title="Cost accuracy, partner performance and $/SF patterns across your projects"
              style={{ background: T.bgAccentSubtle, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent,
                cursor: "pointer", padding: "8px 16px", borderRadius: 6, fontSize: 12, fontFamily: FONT,
                fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 7 }}>
              <Icon.BarChart size={14}/> Portfolio Insights
            </button>
          </div>
        )}

        {/* Project card grid */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: T.textMuted, fontSize: 13 }}>Loading projects...</div>
        ) : (
          <>
            {projects.length === 0 && (
              <div style={{ border: `1px dashed ${T.borderSecondary}`, borderRadius: 6, padding: "40px 32px", marginBottom: 16 }}>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: T.textSubLabel, marginBottom: 6 }}>Welcome to OpenEstimate</div>
                  <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>Get started by creating your first project, importing an existing estimate, or exploring the sandbox.</div>
                </div>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={() => { setShowNewInput(true); }}
                    style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "10px 22px", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 700 }}>
                    + New Project
                  </button>
                  <button onClick={() => { setAiImportName(""); setAiImportAddress(""); setAiImportDuration("12"); setAiImportSF(""); setAiImportFile(null); setShowAiImportModal(true); }}
                    style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "10px 22px", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 500 }}>
                    Import with AI
                  </button>
                  <button onClick={() => setShowBulkImport(true)}
                    title="Load several past estimates at once, each as its own project"
                    style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "10px 22px", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 500 }}>
                    Import Past Projects
                  </button>
                  <button onClick={onSandbox}
                    style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textTertiary, cursor: "pointer", padding: "10px 22px", borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 500 }}>
                    Try Sample Data →
                  </button>
                </div>
              </div>
            )}

            {(() => {
              const filteredProjects = projects.filter(p => {
                if (activeFilter === "active" && !p.isActive) return false;
                if (activeFilter === "inactive" && p.isActive) return false;
                return true;
              });
              const personalProjects = filteredProjects.filter(p => !p.orgId);
              // Collect unique org IDs in the order they first appear
              const orgIds: string[] = [];
              filteredProjects.forEach(p => { if (p.orgId && !orgIds.includes(p.orgId)) orgIds.push(p.orgId); });

              const SectionHeader = ({ label, count }: { label: string; count: number }) => (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, marginTop: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any }}>
                    {label} <span style={{ fontWeight: 400, opacity: .55 }}>({count})</span>
                  </span>
                  <button onClick={() => setShowNewInput(true)}
                    style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "5px 14px", borderRadius: 6, fontSize: 11, fontFamily: FONT, fontWeight: 700, letterSpacing: ".01em" }}>
                    + New Project
                  </button>
                </div>
              );

              const CardGrid = ({ children }: { children: React.ReactNode }) => (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : winW >= 1280 ? "repeat(4,1fr)" : "repeat(3,1fr)", gap: 16, marginBottom: 28 }}>
                  {children}
                </div>
              );

              const renderProjectCard = (p: ProjectSummary) => {
                const accent = cardAccent(p.name, isDarkMode);
                return (
                  <div key={p.id}
                    style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 10, overflow: menuOpenId === p.id ? "visible" : "hidden", cursor: "pointer", position: "relative", transition: "border-color .15s, box-shadow .15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.25)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.borderSecondary; e.currentTarget.style.boxShadow = "none"; }}
                    onClick={() => onOpen(p.id, p.name, p.orgId || null)}>

                    {/* Hero area */}
                    <div style={{ height: 90, background: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
                      {/* The icon stays the empty state -- a grey placeholder
                          box reads as broken, an icon reads as "no photo
                          yet". Centre-cropped at upload; object-fit handles
                          whatever the card's width turns out to be. */}
                      {p.photoPath && photoUrls[p.photoPath] ? (
                        <img src={photoUrls[p.photoPath]} alt=""
                          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
                      ) : (
                        <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke={accent.iconColor} strokeWidth={1.1} strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85, display: "block" }}>
                          <path d="M3 10.5L12 3l9 7.5V21a1 1 0 01-1 1H4a1 1 0 01-1-1V10.5z"/>
                          <path d="M9 22V12h6v10"/>
                        </svg>
                      )}
                      {coverBusy === p.id && (
                        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", fontFamily: FONT }}>
                          Updating photo…
                        </div>
                      )}
                    </div>

                      {/* Outside the hero on purpose. The hero clips to its rounded corners so
                        a cover photo crops cleanly, and a menu inside it was cut
                        off at 90px -- you could see the first two items and
                        nothing else. */}
                      <div style={{ position: "absolute", top: 8, right: 8 }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === p.id ? null : p.id); }}
                          style={{ background: p.photoPath ? "rgba(0,0,0,.45)" : "none", borderRadius: 3, border: "none", color: p.photoPath ? "#fff" : accent.iconColor, cursor: "pointer", opacity: 0.6, padding: 4, display: "flex", lineHeight: 1 }}
                          onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                          onMouseLeave={e => (e.currentTarget.style.opacity = "0.6")}
                        >
                          <Icon.Menu size={14}/>
                        </button>
                        {menuOpenId === p.id && (
                          <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, minWidth: 160, zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", overflow: "hidden" }}>
                            {[
                              { label: "Open", onClick: () => { setMenuOpenId(null); onOpen(p.id, p.name, p.orgId || null); } },
                              ...(p.sharedVia === "owner" ? [{ label: "Share", onClick: () => { setMenuOpenId(null); setSharingProject(p); } }] : []),
                              ...(BUDGET_ENABLED ? [{ label: "Conceptual Budget", onClick: () => { setMenuOpenId(null); onOpenConceptualBudget(p.id); } }] : []),
                              ...(BUDGET_ENABLED ? [{ label: "Construction Budget", onClick: () => { setMenuOpenId(null); setBudgetView({ id: p.id, name: p.name }); } }] : []),
                              { label: p.photoPath ? "Replace Cover Photo" : "Add Cover Photo", onClick: () => { setMenuOpenId(null); pickCoverPhoto(p.id, p.photoPath || null); } },
                              ...(p.photoPath ? [{ label: "Remove Cover Photo", onClick: () => { setMenuOpenId(null); removeCoverPhoto(p.id, p.photoPath as string); } }] : []),
                              { label: "Toggle Active", onClick: async () => { setMenuOpenId(null); await storage.setProjectActive(p.id, !p.isActive); load(); } },
                              { label: p.isActive ? "Archive" : "Restore", onClick: async () => { setMenuOpenId(null); await storage.setProjectActive(p.id, !p.isActive); load(); } },
                              { label: "Delete", danger: true, onClick: () => { setMenuOpenId(null); setConfirmDeleteId(p.id); } },
                            ].map(item => (
                              <button key={item.label} onClick={item.onClick}
                                style={{ width: "100%", background: "none", border: "none", textAlign: "left", padding: "8px 14px", fontSize: 12, color: (item as any).danger ? T.negative : T.textTertiary, cursor: "pointer", fontFamily: FONT, display: "block" }}>
                                {item.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                    {/* Info area */}
                    <div style={{ padding: "11px 13px 13px" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textSubLabel, marginBottom: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", minHeight: 14 }}>
                        {p.address || (p.orgName ? p.orgName : "\u00A0")}
                      </div>
                      {/* Stats row */}
                      <div style={{ display: "flex", paddingTop: 9, borderTop: `1px solid ${T.borderPrimary}` }}>
                        {[
                          { label: "Total", value: p.total > 0 ? fmt(p.total) : "—" },
                          { label: "$/SF", value: p.costPerSF > 0 ? fmt(p.costPerSF) : "—" },
                          { label: "Saved", value: relativeDate(p.savedAt) },
                        ].map(({ label, value }) => (
                          <div key={label} style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" as any, color: T.textWeak, marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: T.textSubLabel, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Delete confirmation overlay */}
                    {confirmDeleteId === p.id && (
                      <div onClick={e => e.stopPropagation()} style={{ position: "absolute", inset: 0, background: T.bgModalBackdrop, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, zIndex: 10, borderRadius: 10 }}>
                        <div style={{ fontSize: 12, color: T.textSecondary, textAlign: "center", padding: "0 16px" }}>Delete "{p.name}"?</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => handleDelete(p.id)} disabled={deletingId === p.id}
                            style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, color: T.dangerText, cursor: "pointer", padding: "7px 16px", borderRadius: 3, fontSize: 12, fontFamily: FONT }}>
                            {deletingId === p.id ? "Deleting..." : "Delete"}
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)}
                            style={{ background: "none", border: `1px solid ${T.borderPrimary}`, color: T.textWeak, cursor: "pointer", padding: "7px 12px", borderRadius: 3, fontSize: 12, fontFamily: FONT }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              };

              const sandboxIconColor = isDarkMode ? "#6a7080" : "#9098a8";
              const sandboxHeroBg   = isDarkMode ? "#1e2028" : "#e8eaee";
              const sandboxCard = (
                <div key="sandbox"
                  style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 10, overflow: "hidden", cursor: "pointer", position: "relative", transition: "border-color .15s, box-shadow .15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.25)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.borderSecondary; e.currentTarget.style.boxShadow = "none"; }}
                  onClick={onSandbox}>

                  {/* Hero area — pencil icon + DEMO label */}
                  <div style={{ height: 90, background: sandboxHeroBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    <svg viewBox="0 0 24 24" width={28} height={28} fill="none" stroke={sandboxIconColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", opacity: 0.85 }}>
                      <path d="M17 3a2.83 2.83 0 014 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                    <div style={{ fontSize: 10, fontWeight: 700, color: sandboxIconColor, letterSpacing: ".1em", opacity: .7 }}>DEMO</div>
                  </div>

                  {/* Info area — mirrors regular project card */}
                  <div style={{ padding: "11px 13px 13px" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textSubLabel, marginBottom: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>Sandbox</div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10, minHeight: 14, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                      Explore a pre-built demo estimate
                    </div>
                    <div style={{ display: "flex", paddingTop: 9, borderTop: `1px solid ${T.borderPrimary}` }}>
                      {([
                        { label: "Total",  value: "$8,540,000" },
                        { label: "$/SF",   value: "$427" },
                        { label: "Status", value: "Demo" },
                      ] as {label:string;value:string}[]).map(({ label, value }) => (
                        <div key={label} style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" as any, color: T.textWeak, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.textSubLabel, fontVariantNumeric: "tabular-nums" }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Personal section */}
                  <SectionHeader label="Personal" count={personalProjects.length + 1} />
                  <CardGrid>
                    {sandboxCard}
                    {personalProjects.map(renderProjectCard)}
                  </CardGrid>

                  {/* Org sections */}
                  {orgIds.map(orgId => {
                    const orgProjects = filteredProjects.filter(p => p.orgId === orgId);
                    const orgName = orgProjects[0]?.orgName || orgId;
                    return (
                      <React.Fragment key={orgId}>
                        <SectionHeader label={orgName} count={orgProjects.length} />
                        <CardGrid>
                          {orgProjects.map(renderProjectCard)}
                        </CardGrid>
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()}
          </>
        )}

        {/* ── CONCEPTUAL BUDGETS LIST ── */}
        <div style={{ marginTop: 48, paddingBottom: 48 }}>
        <div style={{ borderTop: `1px solid ${T.borderSecondary}`, marginBottom: 28 }} />
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexWrap: "wrap", justifyContent: "space-between", gap: isMobile ? 12 : 0, marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.textSubLabel, letterSpacing: "-.01em" }}>Conceptual Budgets</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>
              {cBudgets.filter(b => !b.deletedAt).length} budget{cBudgets.filter(b => !b.deletedAt).length !== 1 ? "s" : ""}
            </div>
          </div>
          {/* Fixed 148px wrapper aligns this button with the Open column in rows below */}
          <div style={{ width: 148, marginRight: isMobile ? 0 : 18 }}>
            <button onClick={createBudget}
              style={{ width: "100%", background: T.borderAccent, border: "none", color: T.textOnGold, cursor: "pointer", padding: "9px 0", borderRadius: 3, fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em" }}>
              + New Budget
            </button>
          </div>
        </div>

        {cBudgets.filter(b => !b.deletedAt).length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", border: `1px dashed ${T.borderSecondary}`, borderRadius: 6 }}>
            <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 6 }}>No conceptual budgets yet</div>
            <div style={{ fontSize: 11, color: T.textWeak }}>Click "+ New Budget" to create an order-of-magnitude estimate</div>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 16 }}>
          {cBudgets.filter(b => !b.deletedAt).map(b => {
            const groups = b.areaGroups || [];
            const totalSF = groups.reduce((s, g) => g.subAreas.reduce((ss, a) => (a.sf || 0) + ss, 0) + s, 0);
            const siteLow = b.siteItems.reduce((s, i) => (i.low || 0) + s, 0);
            const siteHigh = b.siteItems.reduce((s, i) => (i.high || 0) + s, 0);
            const rangeLow = groups.reduce((s, g) => { const sf = g.subAreas.reduce((ss, a) => (a.sf || 0) + ss, 0); return s + sf * (g.lowRate || 0); }, 0) + siteLow;
            const rangeHigh = groups.reduce((s, g) => { const sf = g.subAreas.reduce((ss, a) => (a.sf || 0) + ss, 0); return s + sf * (g.highRate || 0); }, 0) + siteHigh;
            const accent = cardAccent(b.name, isDarkMode);
            const isActiveB = b.isActive !== false;
            return (
              <div key={b.id} style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, overflow: cbMenuOpenId === b.id ? "visible" : "hidden", position: "relative", display: "flex", cursor: "pointer", transition: "border-color .15s, box-shadow .15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHover; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.2)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.borderSecondary; e.currentTarget.style.boxShadow = "none"; }}
                onClick={() => { if (cbMenuOpenId !== b.id && renamingBudgetId !== b.id) onOpenConceptualBudget(b.id); }}>
                {/* Thumbnail */}
                <div style={{ width: 100, flexShrink: 0, background: accent.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "16px 8px" }}>
                  <svg viewBox="0 0 24 24" width={38} height={38} fill="none" stroke={accent.iconColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
                    <path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V10.5z"/>
                    <path d="M9 21V12h6v9"/>
                  </svg>
                  <div style={{ fontSize: 10, fontWeight: 700, color: accent.iconColor, marginTop: 8, opacity: .7, textAlign: "center", letterSpacing: ".05em" }}>
                    {b.name.split(/\s+/).slice(0, 2).map((w: string) => w[0]).join("").toUpperCase()}
                  </div>
                </div>
                {/* Content */}
                <div style={{ flex: 1, padding: "14px 14px 12px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
                  <div>
                    {renamingBudgetId === b.id ? (
                      <input autoFocus value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && renameValue.trim()) { setCBudgets(prev => prev.map(x => x.id === b.id ? { ...x, name: renameValue.trim() } : x)); setRenamingBudgetId(null); }
                          if (e.key === "Escape") setRenamingBudgetId(null);
                        }}
                        onBlur={() => { if (renameValue.trim()) setCBudgets(prev => prev.map(x => x.id === b.id ? { ...x, name: renameValue.trim() } : x)); setRenamingBudgetId(null); }}
                        onClick={e => e.stopPropagation()}
                        style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderAccent}`, borderRadius: 3, color: T.textPrimary, padding: "4px 8px", fontSize: 13, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }} />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textSubLabel, lineHeight: 1.3, marginBottom: 2, overflow: "hidden", display: "-webkit-box" as any, WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any }}>
                        {b.name}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: renamingBudgetId === b.id ? 4 : 0 }}>
                      {totalSF > 0 ? `${totalSF.toLocaleString()} SF · ${groups.length} group${groups.length !== 1 ? "s" : ""}` : "No areas defined"}
                    </div>
                  </div>
                  <div style={{ margin: "8px 0" }}>
                    {rangeLow > 0 || rangeHigh > 0 ? (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.positive }}>{fmt(rangeLow)}</div>
                        <div style={{ fontSize: 11, color: T.textMuted }}>– {fmt(rangeHigh)}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.textWeak }}>No estimate yet</div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 10, color: T.textWeak }}>{b.savedAt ? `Saved ${relativeDate(b.savedAt)}` : ""}</div>
                    {isActiveB
                      ? <div style={{ fontSize: 10, fontWeight: 700, color: "#4abf6a", background: "#4abf6a18", border: "1px solid #4abf6a44", borderRadius: 10, padding: "2px 8px", letterSpacing: ".08em", textTransform: "uppercase" as any }}>● Active</div>
                      : <div style={{ fontSize: 10, fontWeight: 600, color: T.textWeak, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 10, padding: "2px 8px", letterSpacing: ".08em", textTransform: "uppercase" as any }}>○ Inactive</div>
                    }
                  </div>
                </div>
                {/* Three-dot menu */}
                <button onClick={e => { e.stopPropagation(); setCbMenuOpenId(cbMenuOpenId === b.id ? null : b.id); }}
                  style={{ position: "absolute", top: 8, right: 8, background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textMuted, cursor: "pointer", padding: "2px 7px", fontSize: 14, lineHeight: 1, zIndex: 1 }}>
                  ···
                </button>
                {cbMenuOpenId === b.id && (
                  <div style={{ position: "absolute", right: 8, top: 36, zIndex: 100, background: T.bgPanel, border: `1px solid ${T.borderHover}`, borderRadius: 6, boxShadow: "0 6px 24px rgba(0,0,0,0.25)", minWidth: 180, overflow: "hidden", padding: "4px 0" }}
                    onMouseLeave={() => setCbMenuOpenId(null)}>
                    <button onClick={e => { e.stopPropagation(); setRenameValue(b.name); setRenamingBudgetId(b.id); setCbMenuOpenId(null); }}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", color: T.textSecondary, cursor: "pointer", padding: "10px 16px", fontSize: 12, fontFamily: FONT }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                      Rename...
                    </button>
                    <div style={{ height: 1, background: T.borderSecondary }} />
                    <button onClick={e => { e.stopPropagation(); setCBudgets(prev => prev.map(x => x.id === b.id ? { ...x, isActive: !isActiveB } : x)); setCbMenuOpenId(null); }}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", color: T.textSecondary, cursor: "pointer", padding: "10px 16px", fontSize: 12, fontFamily: FONT }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                      {isActiveB ? "Set as Inactive" : "Set as Active"}
                    </button>
                    <div style={{ height: 1, background: T.borderSecondary }} />
                    <button onClick={e => { e.stopPropagation(); setCBudgets(prev => prev.map(x => x.id === b.id ? { ...x, deletedAt: new Date().toISOString() } : x)); setCbMenuOpenId(null); }}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", color: T.negative, cursor: "pointer", padding: "10px 16px", fontSize: 12, fontFamily: FONT }}
                      onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                      Move to Trash
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {/* ── TRASH MODAL ── */}
      {showTrash && (
        <div style={{ position: "fixed", inset: 0, zIndex: 300, background: T.bgModalBackdrop, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => { setShowTrash(false); setPermDeleteId(null); }}>
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderHover}`, borderRadius: 6, width: "100%", maxWidth: 560, maxHeight: "80vh", display: "flex", flexDirection: "column" }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.textSubLabel, display: "flex", alignItems: "center", gap: 6 }}><Icon.Trash size={14}/> Trash</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Items here are not permanently deleted. Restore or remove them.</div>
              </div>
              <button onClick={() => { setShowTrash(false); setPermDeleteId(null); }}
                style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16, padding: "2px 6px", lineHeight: 1, fontFamily: FONT }}>✕</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "16px 24px 24px" }}>
              {loadingTrash && <div style={{ textAlign: "center", padding: "32px 0", color: T.textMuted, fontSize: 13 }}>Loading...</div>}
              {!loadingTrash && deletedProjects.length === 0 && cBudgets.filter(b => !!b.deletedAt).length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: T.textMuted, fontSize: 13 }}>Trash is empty</div>
              )}
              {/* Deleted Projects */}
              {!loadingTrash && deletedProjects.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any, marginBottom: 10 }}>Projects</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24 }}>
                    {deletedProjects.map(p => (
                      <div key={p.id} style={{ background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                          {p.address && <div style={{ fontSize: 11, color: T.textMuted }}>{p.address}</div>}
                          <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>Deleted {relativeDate(p.deletedAt)}</div>
                        </div>
                        {permDeleteId === p.id ? (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={async () => { await storage.permanentlyDeleteProject(p.id); setDeletedProjects(prev => prev.filter(x => x.id !== p.id)); setPermDeleteId(null); }}
                              style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, color: T.dangerText, cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Delete Forever</button>
                            <button onClick={() => setPermDeleteId(null)}
                              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, cursor: "pointer", padding: "5px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={async () => { await storage.restoreProject(p.id); setDeletedProjects(prev => prev.filter(x => x.id !== p.id)); load(); }}
                              style={{ background: T.bgAccentFaint, border: `1px solid ${T.borderAccentFaint}`, color: T.borderAccent, cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontFamily: FONT, fontWeight: 600 }}>Restore</button>
                            <button onClick={() => setPermDeleteId(p.id)}
                              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, cursor: "pointer", padding: "5px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Delete Forever</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {/* Deleted Conceptual Budgets */}
              {!loadingTrash && cBudgets.filter(b => !!b.deletedAt).length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any, marginBottom: 10 }}>Conceptual Budgets</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cBudgets.filter(b => !!b.deletedAt).map(b => (
                      <div key={b.id} style={{ background: T.bgApp, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>{b.name}</div>
                          <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>Deleted {relativeDate(b.deletedAt!)}</div>
                        </div>
                        {permDeleteId === b.id ? (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={() => { setCBudgets(prev => prev.filter(x => x.id !== b.id)); setPermDeleteId(null); }}
                              style={{ background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, color: T.dangerText, cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Delete Forever</button>
                            <button onClick={() => setPermDeleteId(null)}
                              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, cursor: "pointer", padding: "5px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={() => setCBudgets(prev => prev.map(x => x.id === b.id ? { ...x, deletedAt: undefined } : x))}
                              style={{ background: T.bgAccentFaint, border: `1px solid ${T.borderAccentFaint}`, color: T.borderAccent, cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 11, fontFamily: FONT, fontWeight: 600 }}>Restore</button>
                            <button onClick={() => setPermDeleteId(b.id)}
                              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, cursor: "pointer", padding: "5px 10px", borderRadius: 3, fontSize: 11, fontFamily: FONT }}>Delete Forever</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showBulkImport && (
        <BulkImportDialog
          T={T} FONT={FONT} orgId={selectedOrgId || null}
          onClose={() => setShowBulkImport(false)}
          onCreate={async (state, fileName) => {
            // Each file becomes its own project, named from the estimate or
            // the file. Nothing is opened: the point of a bulk run is the
            // portfolio afterwards, not any one job.
            const preset = presets.find((p: any) => p.id === selectedPresetId);
            if (preset) {
              if (!state.overhead) state.overhead = preset.overhead || 10;
              if (!state.profit) state.profit = preset.profit || 8;
              if (!state.contingency) state.contingency = preset.contingency || 5;
            }
            if (!state.projectName) state.projectName = fileName.replace(/\.[^.]+$/, "");
            return await storage.createProject(state);
          }}
          onDone={() => load()}
        />
      )}

      {/* AI Import: hidden file inputs */}
      {/* One hidden input shared by every card; coverForRef says which
          project the chosen file belongs to. */}
      <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => onCoverChosen(e.target.files?.[0])} />
      {editingCover && (
        <CoverPhotoEditor T={T} file={editingCover.file}
          onCancel={() => setEditingCover(null)} onApply={uploadCover} />
      )}
      <input ref={aiFileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" onChange={handleAiImport} style={{ display: "none" }} />
      <input ref={aiImportFileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" style={{ display: "none" }}
        onChange={e => { const f = e.target.files?.[0]; if (f) setAiImportFile(f); e.target.value = ""; }} />

      {/* AI Import: project info modal */}
      {/* ── New Project modal ── */}
      {showNewInput && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: T.bgModalBackdrop, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => { setShowNewInput(false); setNewName(""); setNewAddress(""); setSelectedOrgId(""); }}>
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderAccent}44`, borderRadius: 6, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.borderPrimary}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.borderAccent, display: "flex", alignItems: "center", gap: 6 }}><Icon.Plus size={14}/> New Project</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>Create a blank estimate from a preset template.</div>
              </div>
              <button onClick={() => { setShowNewInput(false); setNewName(""); setNewAddress(""); setSelectedOrgId(""); }}
                style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "2px 6px", lineHeight: 1, display: "flex" }}>
                <Icon.X size={14}/>
              </button>
            </div>
            {/* Body */}
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column" as any, gap: 16 }}>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any, marginBottom: 5 }}>Project Name</div>
                <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && newName.trim()) handleCreate(); if (e.key === "Escape") { setShowNewInput(false); setNewName(""); setNewAddress(""); setSelectedOrgId(""); } }}
                  placeholder="e.g. 1452 Rosewood Residence"
                  style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                  onFocus={e => e.target.style.borderColor = T.borderAccent}
                  onBlur={e => e.target.style.borderColor = T.borderSecondary} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any, marginBottom: 5 }}>Address / Location</div>
                <input value={newAddress} onChange={e => setNewAddress(e.target.value)}
                  placeholder="e.g. 123 Main St, Portland OR"
                  style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                  onFocus={e => e.target.style.borderColor = T.borderAccent}
                  onBlur={e => e.target.style.borderColor = T.borderSecondary} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any }}>Start from Preset</div>
                  <button onClick={() => { setShowNewInput(false); setNewName(""); setNewAddress(""); onOpenSettings("settings-presets"); }}
                    style={{ background: "none", border: "none", color: T.borderAccent, cursor: "pointer", fontSize: 10, padding: 0, fontFamily: FONT }}>
                    Manage Presets →
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column" as any, gap: 5, maxHeight: 200, overflowY: "auto" as any }}>
                  {presets.map((p: any) => {
                    const on = selectedPresetId === p.id;
                    return (
                      <div key={p.id} onClick={() => setSelectedPresetId(p.id)}
                        style={{ padding: "9px 12px", borderRadius: 3, cursor: "pointer", border: on ? `1px solid ${T.borderAccentFaint}` : `1px solid ${T.borderSecondary}`, background: on ? T.bgAccentFaint : T.bgApp, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 12, fontWeight: on ? 600 : 400, color: on ? T.borderAccent : T.textMuted }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: T.textWeak, whiteSpace: "nowrap" as any }}>OH {p.overhead}% · Profit {p.profit}% · Cont {p.contingency}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {userOrgs.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any, marginBottom: 5 }}>Create In</div>
                  <select value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}
                    style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textSecondary, padding: "8px 10px", fontSize: 12, fontFamily: FONT, outline: "none", boxSizing: "border-box" as any }}>
                    <option value="">Personal</option>
                    {userOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                  <div style={{ fontSize: 10, color: T.textPlaceholder, marginTop: 4 }}>
                    {selectedOrgId ? "All organization members will be able to access this project." : "Only you will have access unless you share it."}
                  </div>
                </div>
              )}
            </div>
            {/* Footer */}
            <div style={{ padding: "0 24px 20px", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setShowNewInput(false); setNewName(""); setNewAddress(""); setSelectedOrgId(""); }}
                style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, cursor: "pointer", padding: "8px 18px", borderRadius: 3, fontSize: 13, fontFamily: FONT }}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating || !newName.trim()}
                style={{ background: newName.trim() ? T.borderAccent : T.bgInput, border: "none", color: newName.trim() ? T.textOnGold : T.textMuted, cursor: creating || !newName.trim() ? "not-allowed" : "pointer", padding: "8px 22px", borderRadius: 3, fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em", transition: "background .15s" }}>
                {creating ? "Creating..." : "Create Project →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAiImportModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: T.bgModalBackdrop, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          onClick={() => setShowAiImportModal(false)}>
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderAccent}44`, borderRadius: 6, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.borderPrimary}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.borderAccent, display: "flex", alignItems: "center", gap: 6 }}><Icon.Bot size={14}/> Import Estimate with AI</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>Claude AI maps your file to CSI divisions. Provide project details below.</div>
              </div>
              <button onClick={() => setShowAiImportModal(false)}
                style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16, padding: "2px 6px", lineHeight: 1, fontFamily: FONT }}>✕</button>
            </div>
            {/* Body */}
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: isMobile ? "column" as any : "row" as any, gap: 24 }}>
              {/* Left: project metadata */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column" as any, gap: 14 }}>
                <div>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>Project Name</div>
                  <input value={aiImportName} onChange={e => setAiImportName(e.target.value)} placeholder="e.g. Willow Creek Residence"
                    style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                    onFocus={e => e.target.style.borderColor = T.borderAccent}
                    onBlur={e => e.target.style.borderColor = T.borderSecondary} />
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>Leave blank to use AI-detected name</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>Address / Location</div>
                  <input value={aiImportAddress} onChange={e => setAiImportAddress(e.target.value)} placeholder="e.g. 123 Main St, Portland OR"
                    style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                    onFocus={e => e.target.style.borderColor = T.borderAccent}
                    onBlur={e => e.target.style.borderColor = T.borderSecondary} />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>Duration (months)</div>
                    <input type="number" value={aiImportDuration} onChange={e => setAiImportDuration(e.target.value)} min={1} max={120}
                      style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                      onFocus={e => e.target.style.borderColor = T.borderAccent}
                      onBlur={e => e.target.style.borderColor = T.borderSecondary} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>Building Area (SF)</div>
                    <input type="number" value={aiImportSF} onChange={e => setAiImportSF(e.target.value)} min={0} placeholder="Optional"
                      style={{ width: "100%", boxSizing: "border-box" as any, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none" }}
                      onFocus={e => e.target.style.borderColor = T.borderAccent}
                      onBlur={e => e.target.style.borderColor = T.borderSecondary} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>Preset (OH / Profit / Contingency)</div>
                  <select value={aiImportPresetId} onChange={e => setAiImportPresetId(e.target.value)}
                    style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "8px 10px", fontSize: 13, color: T.textPrimary, fontFamily: FONT, outline: "none", cursor: "pointer" }}>
                    {presets.map((p: any) => (
                      <option key={p.id} value={p.id}>{p.name} — OH {p.overhead}% · Profit {p.profit}% · Cont {p.contingency}%</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Right: file drop zone */}
              <div style={{ width: isMobile ? "100%" : 200, flexShrink: 0 }}>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 5 }}>File to Import</div>
                <div
                  onClick={() => aiImportFileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setAiImportDragging(true); }}
                  onDragLeave={() => setAiImportDragging(false)}
                  onDrop={e => { e.preventDefault(); setAiImportDragging(false); const f = e.dataTransfer.files[0]; if (f) setAiImportFile(f); }}
                  style={{ border: `2px dashed ${aiImportDragging ? T.borderAccent : aiImportFile ? T.positive : T.borderSecondary}`, borderRadius: 6, padding: "20px 12px", textAlign: "center", cursor: "pointer", background: aiImportDragging ? T.bgAccentFaint : "transparent", transition: "border-color .15s, background .15s", minHeight: 120, display: "flex", flexDirection: "column" as any, alignItems: "center", justifyContent: "center", gap: 8 }}>
                  {aiImportFile ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "center" }}><Icon.FileText size={14}/></div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.positive, wordBreak: "break-word" as any }}>{aiImportFile.name}</div>
                      <div style={{ fontSize: 10, color: T.textMuted }}>{(aiImportFile.size / 1024).toFixed(0)} KB · Click to change</div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "center" }}><Icon.Folder size={14}/></div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>Drop file here<br/>or click to browse</div>
                      <div style={{ fontSize: 10, color: T.textWeak }}>XLSX · CSV · PDF</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: "0 24px 20px", display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
              {!aiImportFile && <div style={{ fontSize: 11, color: T.textWeak, flex: 1 }}>Select a file to enable import</div>}
              <button onClick={() => setShowAiImportModal(false)}
                style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted, cursor: "pointer", padding: "8px 18px", borderRadius: 3, fontSize: 13, fontFamily: FONT }}>
                Cancel
              </button>
              <button disabled={!aiImportFile}
                onClick={() => { if (!aiImportFile) return; runAiImport(aiImportFile, { name: aiImportName, address: aiImportAddress, duration: parseInt(aiImportDuration) || 12, sf: parseFloat(aiImportSF) || 0, presetId: aiImportPresetId }); }}
                style={{ background: aiImportFile ? T.borderAccent : T.bgInput, border: "none", color: aiImportFile ? T.textOnGold : T.textMuted, cursor: aiImportFile ? "pointer" : "not-allowed", padding: "8px 22px", borderRadius: 3, fontSize: 13, fontFamily: FONT, fontWeight: 700, letterSpacing: ".02em", transition: "background .15s" }}>
                Analyze with AI →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Import: loading overlay */}
      {aiImporting && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: T.bgModalBackdrop, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderAccent}44`, borderRadius: 6, padding: isMobile ? "28px 20px" : "40px 52px", textAlign: "center", maxWidth: `min(100vw - 32px, 360px)` }}>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Icon.Bot size={14}/></div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.borderAccent, marginBottom: 8 }}>Analyzing Your Estimate</div>
            <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
              Claude AI is reading your file and mapping line items to CSI MasterFormat divisions. This usually takes 10–20 seconds.
            </div>
            <div style={{ marginTop: 20, height: 2, background: T.borderPrimary, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: "60%", background: T.borderAccent, borderRadius: 3, animation: "aiPulse 2s ease-in-out infinite" }} />
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes aiPulse{0%{width:20%}50%{width:80%}100%{width:20%}}`}</style>

      {/* Share dialog */}
      {sharingProject && (
        <ShareDialog
          projectId={sharingProject.id}
          projectName={sharingProject.name}
          ownerId={sharingProject.ownerId}
          ownerName={sharingProject.ownerName}
          currentOrgId={sharingProject.orgId}
          currentOrgName={sharingProject.orgName}
          storage={storage}
          userOrgs={userOrgs}
          theme={T}
          isOwner={sharingProject.sharedVia === "owner"}
          onClose={() => setSharingProject(null)}
          onChanged={load}
        />
      )}

      {/* Privacy & Data modal */}
      {showPrivacyModal && (
        <PrivacyModal
          onClose={() => setShowPrivacyModal(false)}
          onAccountDeleted={() => { setShowPrivacyModal(false); onSignOut(); }}
          exportMyData={() => (storage as any).exportMyData?.() ?? Promise.resolve({ error: "Not available" })}
          deleteMyData={() => (storage as any).deleteMyData?.() ?? Promise.resolve({ error: "Not available" })}
        />
      )}

      {/* Copyright footer */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, textAlign: "center", padding: "10px 0", fontSize: 11, color: T.textMuted, letterSpacing: ".02em", opacity: 0.6, display: isMobile ? "none" : undefined }}>
        © 2026 OpenEstimate. All rights reserved.
      </div>

      {/* ── Portfolio AI Assistant ── */}
      {showAssistant && (
        <ProjectAssistant
          storage={storage}
          projects={projects}
          onClose={() => setShowAssistant(false)}
        />
      )}
      {showOrgDirectory && (
        <OrgDirectory
          isDarkMode={isDarkMode}
          onClose={() => setShowOrgDirectory(false)}
        />
      )}
      {showHelp && <HelpCenter T={T} onClose={() => setShowHelp(false)} />}
    </div>
  );
}
